<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import type { VoterSummary } from '../lib/stores/voters';
  import { ApiError, humanizeError, apiFetchAuthed, handleSessionExpired } from '$lib/api';
  import { toast } from '$lib/toast';

  export let voter: VoterSummary;

  const dispatch = createEventDispatcher<{ close: void; saved: VoterSummary }>();

  // -- form state --
  // Default to 'door' — the overwhelmingly common canvass method — so the
  // volunteer only has to pick a result to save (fast path).
  let method: string = 'door';
  let result: string = '';
  let supportLevel: number | null = null;
  let yardSign = false;
  let volunteerInterest = false;
  let donationInterest = false;
  let donated = false;
  let volunteered = false;
  let selectedTags: Set<string> = new Set();
  let issueOther = '';
  let scheduleCallback = false;
  let callbackAt: string = '';
  let note = '';
  let saving = false;
  let error = '';

  // -- issue tags --
  interface IssueTag { name: string; display_name: string; sort_order: number; }
  let issueTags: IssueTag[] = [];

  export async function loadIssueTags(): Promise<void> {
    let res: Response;
    try {
      res = await apiFetchAuthed('/api/issue_tags');
    } catch {
      return;  // 401 already bounced to sign-in; nothing typed yet to lose
    }
    if (res.ok) {
      const data = await res.json();
      issueTags = data.items;
    }
  }

  // -- offline-safety: stash unsaved form state per-voter so a session timeout
  // (or a dropped connection in the field) doesn't lose what the volunteer typed.
  const stashKey = `canvass.pendingContact.${voter.id}`;

  function stashForm(): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(
        stashKey,
        JSON.stringify({
          method,
          result,
          supportLevel,
          yardSign,
          volunteerInterest,
          donationInterest,
          tags: [...selectedTags],
          issueOther,
          scheduleCallback,
          callbackAt,
          note,
        }),
      );
    } catch { /* quota / private-mode — best effort */ }
  }

  function clearStash(): void {
    if (typeof sessionStorage === 'undefined') return;
    try { sessionStorage.removeItem(stashKey); } catch { /* ignore */ }
  }

  function restoreStash(): void {
    if (typeof sessionStorage === 'undefined') return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem(stashKey); } catch { return; }
    if (!raw) return;
    try {
      const s = JSON.parse(raw);
      if (typeof s.method === 'string') method = s.method;
      if (typeof s.result === 'string') result = s.result;
      supportLevel = s.supportLevel ?? null;
      yardSign = !!s.yardSign;
      volunteerInterest = !!s.volunteerInterest;
      donationInterest = !!s.donationInterest;
      selectedTags = new Set(Array.isArray(s.tags) ? s.tags : []);
      issueOther = s.issueOther ?? '';
      scheduleCallback = !!s.scheduleCallback;
      callbackAt = s.callbackAt ?? '';
      note = s.note ?? '';
    } catch { /* corrupt stash — ignore and start fresh */ }
  }

  // Restore at init (not onMount — onMount does not fire in the jsdom test env).
  restoreStash();

  // Keep the draft on the device while the volunteer types (codexms #2546).
  // Any 401 on ANY request (not just Save) sends them to sign-in and unmounts
  // this form; stashing only on Save failure lost whatever was typed first.
  // Stops once the contact is saved, so a saved entry is never stashed back.
  let saved = false;
  function isDirty(): boolean {
    return result !== '' || note.trim() !== '' || issueOther.trim() !== '' ||
      selectedTags.size > 0 || yardSign || volunteerInterest || donationInterest ||
      scheduleCallback || supportLevel !== null;
  }
  $: if (!saved) {
    // Referenced so Svelte re-runs this on every field change.
    void [method, result, supportLevel, yardSign, volunteerInterest, donationInterest,
          selectedTags, issueOther, scheduleCallback, callbackAt, note];
    // Erased back to a blank form: drop the old draft too, or it would come
    // back on the next open (codexms follow-up to #2546).
    if (isDirty()) stashForm(); else clearStash();
  }

  // Closing the form on purpose discards the draft; only an unplanned unmount
  // (expiry redirect, reload) should leave it for the next open.
  function closeForm(): void {
    clearStash();
    dispatch('close');
  }

  onMount(() => { loadIssueTags(); });

  const METHODS = ['door', 'phone', 'text', 'mail', 'event'];
  const METHOD_RESULTS: Record<string, { value: string; label: string }[]> = {
    door:  [{ value: 'spoke', label: 'Spoke' }, { value: 'not_home', label: 'Not home' }, { value: 'refused', label: 'Refused' }, { value: 'moved', label: 'Moved' }, { value: 'wrong_address', label: 'Wrong address' }],
    phone: [{ value: 'spoke', label: 'Spoke' }, { value: 'no_response', label: 'No response' }, { value: 'refused', label: 'Refused' }, { value: 'wrong_number', label: 'Wrong number' }],
    text:  [{ value: 'spoke', label: 'Spoke' }, { value: 'no_response', label: 'No response' }, { value: 'refused', label: 'Refused' }, { value: 'wrong_number', label: 'Wrong number' }],
    mail:  [{ value: 'sent', label: 'Sent' }, { value: 'returned_undeliverable', label: 'Returned undeliverable' }],
    event: [{ value: 'spoke', label: 'Spoke' }, { value: 'signed_petition', label: 'Signed petition' }],
  };
  $: results = METHOD_RESULTS[method] ?? [];
  $: if (result && !results.some(r => r.value === result)) { result = ''; resetResult(); }
  const SUPPORT_LABELS = ['', 'Strong against', 'Lean against', 'Undecided', 'Lean support', 'Strong support'];

  $: needsSupport = result === 'spoke' && supportLevel === null;
  $: needsCallbackDate = scheduleCallback && callbackAt === '';
  $: canSave = method !== '' && result !== '' && !needsSupport && !needsCallbackDate;

  function toggleTag(name: string) {
    const next = new Set(selectedTags);
    next.has(name) ? next.delete(name) : next.add(name);
    selectedTags = next;
  }

  function resetResult() {
    supportLevel = null;
  }

  async function save() {
    if (!canSave || saving) return;
    saving = true;
    error = '';
    try {
      const body: Record<string, unknown> = {
        voter_id: voter.id,
        contact_method: method,
        contact_result: result,
        support_level: result === 'spoke' ? supportLevel : null,
        yard_sign_requested: yardSign || null,
        volunteer_interest: volunteerInterest || null,
        donation_interest: donationInterest || null,
        donated: donated || null,
        volunteered: volunteered || null,
        callback_at: scheduleCallback && callbackAt ? new Date(callbackAt).toISOString() : null,
        note: note.trim() || null,
        issue_tags: [...selectedTags],
      };
      // Trimmed, and the key is omitted entirely when blank.
      const otherIssue = issueOther.trim();
      if (otherIssue) body.issue_other = otherIssue;
      const res = await fetch('/api/contacts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        // Session expired mid-canvass. Keep the entry on the device so the
        // volunteer can sign back in and re-save without retyping.
        stashForm();
        error = 'Your session expired — sign in again, your entry is saved on this phone.';
        // Stash first, then bounce: the restore on reopen brings the entry back.
        await handleSessionExpired();
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new ApiError(res.status, JSON.stringify(d));
      }
      // Optimistically patch voter object for pin color + detail display
      // Mirror the server's sticky-spoke rule: a non-spoke contact does not
      // override an existing spoke, so the pin doesn't flip then snap back.
      const keepSpoke = result !== 'spoke' && voter.latest_contact_result === 'spoke';
      const updated: VoterSummary = keepSpoke ? { ...voter } : {
        ...voter,
        latest_contact_result: result,
        latest_support_level: result === 'spoke' ? supportLevel : null,
        latest_contact_at: new Date().toISOString(),
      };
      // Outcome flags stick to the voter regardless of the sticky-spoke rule.
      if (donated) updated.has_donated = true;
      if (volunteered) updated.has_volunteered = true;
      saved = true;
      clearStash();
      dispatch('saved', updated);
      toast('Contact saved', 'success');
    } catch (e: unknown) {
      // Any failure (network error included) — stash so nothing is lost.
      stashForm();
      error = e instanceof ApiError
        ? humanizeError(e, 'Could not save — please try again.')
        : 'Could not save — please try again. Your entry is saved on this phone.';
    } finally {
      saving = false;
    }
  }
</script>

<!-- Backdrop. Disabled while saving so a stray tap can't close the modal
     after the user hit Save — that would silently produce a duplicate contact
     when the volunteer thinks it failed and re-records. -->
<button
  class="backdrop"
  on:click={() => { if (!saving) closeForm(); }}
  aria-label="Close record contact"
  disabled={saving}
/>

<section class="modal" role="dialog" aria-modal="true" aria-label="Record contact">
  <div class="modal-header">
    <h2>Record contact</h2>
    <button
      class="close-btn"
      on:click={closeForm}
      aria-label="Close"
      disabled={saving}
    >✕</button>
  </div>
  <div class="modal-body">

    <!-- Method -->
    <fieldset role="group" aria-label="Method">
      <legend>Method</legend>
      <div class="pill-row">
        {#each METHODS as m}
          <button
            class="pill" class:active={method === m}
            on:click={() => (method = m)}
            aria-pressed={method === m}
          >{m.charAt(0).toUpperCase() + m.slice(1)}</button>
        {/each}
      </div>
    </fieldset>

    <!-- Result -->
    <fieldset role="group" aria-label="Result">
      <legend>Result</legend>
      <div class="pill-row">
        {#each results as r}
          <button
            class="pill" class:active={result === r.value}
            on:click={() => { if (result !== r.value) { result = r.value; resetResult(); } }}
            aria-pressed={result === r.value}
          >{r.label}</button>
        {/each}
      </div>
    </fieldset>

    <!-- Support level (spoke only) -->
    {#if result === 'spoke'}
      <fieldset role="group" aria-label="Support level">
        <legend>Support level</legend>
        <div class="pill-row support-row">
          {#each [1,2,3,4,5] as lvl}
            <button
              class="pill support-pill"
              class:active={supportLevel === lvl}
              on:click={() => (supportLevel = lvl)}
              aria-pressed={supportLevel === lvl}
              title={SUPPORT_LABELS[lvl]}
            >{lvl} — {SUPPORT_LABELS[lvl]}</button>
          {/each}
        </div>
      </fieldset>
    {/if}

    <!-- Checkboxes -->
    <div class="checkboxes">
      <label class="check-label">
        <input type="checkbox" bind:checked={yardSign} /> Yard sign requested
      </label>
      <label class="check-label">
        <input type="checkbox" bind:checked={volunteerInterest} /> Volunteer interest
      </label>
      <label class="check-label">
        <input type="checkbox" bind:checked={donationInterest} /> Donation interest
      </label>
      <label class="check-label">
        <input type="checkbox" bind:checked={donated} /> Donated
      </label>
      <label class="check-label">
        <input type="checkbox" bind:checked={volunteered} /> Signed up to volunteer
      </label>
    </div>

    <!-- Issue tags -->
    {#if issueTags.length > 0}
      <div class="tags-section">
        <p class="tags-label">Issues</p>
        <div class="tags-row">
          {#each issueTags as tag}
            <button
              class="tag-chip" class:selected={selectedTags.has(tag.name)}
              on:click={() => toggleTag(tag.name)}
              aria-pressed={selectedTags.has(tag.name)}
            >{tag.display_name}</button>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Other issue. Always rendered, including when the tag fetch failed or
         has not resolved yet, because free text is the only way to capture an
         issue that is not on the list. -->
    <div class="issue-other-section">
      <label class="issue-other-label" for="contact-issue-other">Other issue</label>
      <input
        id="contact-issue-other"
        class="issue-other-input"
        type="text"
        maxlength="200"
        placeholder="Not on the list? Type it here"
        bind:value={issueOther}
      />
    </div>

    <!-- Callback -->
    <div class="callback-section">
      <label class="check-label">
        <input type="checkbox" bind:checked={scheduleCallback} aria-label="Schedule callback" /> Schedule callback
      </label>
      {#if scheduleCallback}
        <label class="datetime-label" for="callback-dt">Callback date</label>
        <input
          id="callback-dt"
          type="datetime-local"
          bind:value={callbackAt}
          aria-label="Callback date"
          class="datetime-input"
        />
      {/if}
    </div>

    <!-- Note -->
    <label class="note-label" for="contact-note">Note</label>
    <textarea
      id="contact-note"
      class="note-textarea"
      rows="3"
      maxlength="2000"
      placeholder="Optional notes…"
      bind:value={note}
    ></textarea>

    {#if error}
      <p class="error-msg" role="alert">{error}</p>
    {/if}
  </div>

  <div class="modal-footer">
    {#if needsSupport}
      <p class="save-hint">Pick a support level above to save.</p>
    {:else if needsCallbackDate}
      <p class="save-hint">Add a callback date above to save.</p>
    {/if}
    <button
      class="btn btn-primary"
      disabled={!canSave || saving}
      on:click={save}
    >{saving ? 'Saving…' : 'Save'}</button>
  </div>
</section>

<style>
  .backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,.4);
    z-index: 1000; border: none; cursor: default;
  }
  .modal {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: white; border-radius: 1rem 1rem 0 0;
    z-index: 1001; display: flex; flex-direction: column;
    max-height: 90dvh;
  }
  .modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 1rem 0.5rem; border-bottom: 1px solid #eee;
    flex-shrink: 0;
  }
  .modal-header h2 { margin: 0; font-size: 1.1rem; }
  .close-btn {
    background: none; border: none; font-size: 1.2rem; cursor: pointer;
    min-width: 44px; min-height: 44px; line-height: 1;
  }
  .modal-body { padding: 1rem; overflow-y: auto; flex: 1; }
  .modal-footer {
    padding: 0.75rem 1rem;
    padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
    border-top: 1px solid #eee; flex-shrink: 0;
  }
  .save-hint { margin: 0 0 0.5rem; font-size: 0.8rem; color: #6b7280; text-align: center; }
  fieldset { border: none; padding: 0; margin: 0 0 1rem; }
  legend { font-weight: 600; font-size: 0.9rem; margin-bottom: 0.4rem; }
  .pill-row { display: flex; flex-wrap: wrap; gap: 0.4rem; }
  .pill {
    padding: 0.45rem 0.85rem; border-radius: 999px;
    border: 1.5px solid #ccc; background: white; cursor: pointer;
    font-size: 0.85rem; min-height: 44px; white-space: nowrap;
    transition: all .15s;
  }
  .pill.active { background: #1a73e8; color: white; border-color: #1a73e8; }
  .support-row { flex-direction: column; }
  .support-pill { border-radius: 8px; text-align: left; }
  .checkboxes { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
  .check-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; cursor: pointer; min-height: 44px; }
  .check-label input[type=checkbox] { width: 18px; height: 18px; flex-shrink: 0; cursor: pointer; }
  .tags-section { margin-bottom: 1rem; }
  .tags-label { font-weight: 600; font-size: 0.9rem; margin-bottom: 0.4rem; }
  .tags-row { display: flex; flex-wrap: wrap; gap: 0.4rem; }
  .tag-chip {
    padding: 0.35rem 0.75rem; border-radius: 999px;
    border: 1.5px solid #ccc; background: white; cursor: pointer;
    font-size: 0.85rem; min-height: 44px;
  }
  .tag-chip.selected { background: #e8f0fe; border-color: #1a73e8; color: #1a73e8; }
  .issue-other-section { margin-bottom: 1rem; }
  .issue-other-label { display: block; font-weight: 600; font-size: 0.9rem; margin-bottom: 0.3rem; }
  .issue-other-input {
    width: 100%; padding: 0.5rem; border: 1.5px solid #ccc; border-radius: 8px;
    font-size: 1rem; min-height: 44px; box-sizing: border-box;
  }
  .callback-section { margin-bottom: 1rem; }
  .datetime-label { display: block; font-size: 0.85rem; margin: 0.4rem 0 0.2rem; }
  .datetime-input { width: 100%; padding: 0.5rem; border: 1.5px solid #ccc; border-radius: 8px; font-size: 1rem; }
  .note-label { display: block; font-weight: 600; font-size: 0.9rem; margin-bottom: 0.3rem; }
  .note-textarea { width: 100%; padding: 0.5rem; border: 1.5px solid #ccc; border-radius: 8px; font-size: 1rem; resize: vertical; }
  .error-msg { color: #dc2626; font-size: 0.9rem; margin-top: 0.5rem; }
  .btn {
    width: 100%; padding: 0.75rem; border-radius: 8px; border: none;
    font-size: 1rem; font-weight: 600; min-height: 44px; cursor: pointer;
  }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-primary { background: #1a73e8; color: white; }
  .btn-primary:hover:not(:disabled) { background: #1557b0; }
</style>
